import type { FC } from 'react';
import cn from 'classnames';
import { Button, FormHelperText, Modal } from '@selfinspectionorg/ui-kit';
import type { KeyedMutator } from 'swr';
import type { IMfaStatusView } from '@shared/services';
import styles from './mfa.module.scss';
import { useTotpSetup } from './useTotpSetup';
import { useCodeFactorDisable } from './useCodeFactorDisable';
import { StepUpModal } from './StepUpModal';
import PlusIcon from './assets/plus.svg?react';
import { SectionHeading } from '@/modules/profile/components';
import { BackupCodes, TotpSetupView } from '@/modules/auth/features/mfa/components';

interface Props {
  hasTotp: boolean;
  /** Whether the email factor is enabled; when true, authenticator setup is not offered from here. */
  hasEmail: boolean;
  mutate: KeyedMutator<IMfaStatusView>;
}

/**
 * Authenticator (TOTP) section of the Profile Security tab. When the factor is not enabled it drives an
 * in-page setup wizard over the session-authorized management API: step-up password → QR / secret +
 * code → one-time backup codes. When enabled it offers disabling the factor behind a confirmation
 * modal — the disable endpoint takes no step-up password, so the explicit confirm is the only friction.
 * The code factors are mutually exclusive, so setup is only offered while the email factor is also off —
 * with email codes on, this section stays status-only. Behaviour is unchanged from the retired card.
 */
export const AuthenticatorSection: FC<Props> = ({ hasTotp, hasEmail, mutate }) => {
  const setup = useTotpSetup(mutate);
  const disable = useCodeFactorDisable(mutate);

  return (
    <section className={styles.section}>
      <SectionHeading title="Authenticator app" description="Time-based one-time codes from an authenticator app." />
      <span
        className={cn(styles.status, hasTotp ? styles.statusEnabled : styles.statusDisabled)}
        data-testid="authenticator-status"
      >
        {hasTotp ? 'Enabled' : 'Not enabled'}
      </span>

      {hasTotp ? (
        <div className={styles.actions}>
          <Button variant="danger" width="100%" onClick={disable.open} data-testid="disable-authenticator-button">
            Disable
          </Button>
        </div>
      ) : (
        !hasEmail && (
          <div className={styles.actions}>
            <Button onClick={setup.open} width="100%" data-testid="setup-authenticator-button">
              <span className={styles.buttonContent}>
                <PlusIcon />
                Set up
              </span>
            </Button>
          </div>
        )
      )}

      <StepUpModal
        isOpen={setup.step === 'stepup'}
        title="Confirm it's you"
        description="Enter your current password to set up your authenticator app."
        password={setup.stepUp.password}
        onPasswordChange={setup.stepUp.setPassword}
        onSubmit={setup.submitStepUp}
        onClose={setup.close}
        isVerifying={setup.stepUp.isVerifying}
        error={setup.stepUp.error}
      />

      <Modal.Wrapper isOpen={setup.step === 'setup'} onClose={setup.close} id="totpSetupModal" width="480px">
        <Modal.Title
          titleText="Set up an authenticator app"
          additionalText="Scan the QR code with your authenticator app, or enter the key manually."
        />
        <TotpSetupView
          otpauthUri={setup.setupView?.otpauthUri}
          secret={setup.setupView?.secret}
          code={setup.code}
          onCodeChange={setup.setCode}
          onSubmit={setup.submitCode}
          isSubmitting={setup.isVerifying}
          error={setup.verifyError}
        />
      </Modal.Wrapper>

      {/* Factor is enrolled by this point, so close = revalidate; the codes are shown exactly once. */}
      <Modal.Wrapper isOpen={setup.step === 'backup'} onClose={setup.finish} id="totpBackupModal" width="480px">
        <BackupCodes codes={setup.backupCodes} onAcknowledge={setup.finish} />
      </Modal.Wrapper>

      <Modal.Wrapper isOpen={disable.isConfirming} onClose={disable.close} id="disableTotpModal" width="400px">
        <Modal.Title
          titleText="Disable authenticator app?"
          additionalText={
            "You'll no longer be asked for a code when signing in, and your backup codes will stop " +
            'working. This cannot be undone.'
          }
        />
        {disable.error && (
          <FormHelperText errored data-testid="disable-authenticator-error">
            {disable.error}
          </FormHelperText>
        )}
        <Modal.ActionButtons onCancelButtonClick={disable.close}>
          <Button
            variant="danger"
            width="132px"
            onClick={disable.disable}
            isLoading={disable.isDisabling}
            data-testid="confirm-disable-authenticator-button"
          >
            Disable
          </Button>
        </Modal.ActionButtons>
      </Modal.Wrapper>
    </section>
  );
};
